// AM Advertising Home — the account managers' daily triage dashboard.
//
// Answers "is my book on track, and if not, where do I look?" in ~30 seconds:
// alerts + verdict first, then the fixed performance pulse, the book table,
// wins & worries, the channel comparison and the best-creatives gallery. A
// TRIAGE page — every row deep-links into the two reporting dashboards, which
// stay the analysis surfaces.
//
// Alert evaluation runs CLIENT-SIDE off the overview spine through the pure
// shared core (src/lib/advertisingHome) — the same core Phase 2's Slack digest
// will run server-side.

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { PageHeader } from '../../components/layout/PageHeader';
import { SelectField } from '../../components/ui/SelectField';
import { Button } from '../../components/ui/button';
import { presetToRange, type DateRange } from '../../lib/reportingDateRange';
import { formatCurrencyAmounts } from '../../lib/reportingMoney';
import { normalizeDisplayCurrency } from '../../lib/reporting/currencyConversion';
import { comparisonRange } from '../../lib/advertisingHome/comparisonRange';
import {
  EMPTY_HOME_FILTER,
  accountFacetsFromBook,
  filterBookAccounts,
  filterRowsToAccounts,
  type HomeFilterSelection,
} from '../../lib/advertisingHome/bookFilter';
import { evaluateAlerts, type AlertType } from '../../lib/advertisingHome/alerts';
import { BrandAverages } from '../../lib/advertisingHome/noLeads';
import { ALL_AD_CHANNELS } from '../../lib/advertisingHome/channelLanes';
import { groupNotesByAlert } from '../../lib/advertisingHome/alertNotes';
import { computeChannelPacing } from '../../lib/advertisingHome/alerts';
import {
  ALL_ALERT_TYPES,
  type AlertPlatformFilter,
} from '../../lib/advertisingHome/alertsView';
import { buildPulse } from '../../lib/advertisingHome/pulse';
import {
  PULSE_CHANNEL_OPTIONS,
  PULSE_METRIC_OPTIONS,
  isPulseChannel,
  isPulseMetric,
} from '../../lib/advertisingHome/pulseSeries';
import { channelDays, indexSpine } from '../../lib/advertisingHome/spine';
import { buildBookRows, sortBookRows, type BookSortKey } from '../../lib/advertisingHome/bookRows';
import { rankCplChanges } from '../../lib/advertisingHome/cplChange';
import type { ComparisonMode } from '../../types/advertisingHome';
import type { ReportingDatePreset } from '../../types/reporting';
import { useCurrentUser } from '../auth';
import { patchMyPreferences } from '../accounts';
import { DateRangePicker } from '../reporting/DateRangePicker';
import { getFxRates } from '../reporting/api';
import { ConversionNote, DisplayCurrencySelect } from '../reporting/DisplayCurrencySelect';
import { PanelTitle } from '../reporting/PanelTitle';
import {
  acknowledgeAlert,
  createAlertNote,
  getHomeBook,
  getHomeCreatives,
  getHomeOverview,
  listAcknowledgements,
  listAlertNotes,
  setSlackDigestEnabled,
  unacknowledgeAlert,
} from './api';
import {
  alertNoteKey,
  applyAcknowledgements,
  type AcknowledgedAlert,
} from '../../lib/advertisingHome/acknowledgements';
import type { HomeAlert } from '../../lib/advertisingHome/alerts';
import {
  ADVERTISING_HOME_GRID_KEY,
  COMPARISON_MODE_OPTIONS,
  decodeHomeViewState,
  encodeHomeViewState,
  type HomeViewState,
} from './homePrefs';
import { AlertsPanel } from './AlertsPanel';
import { AlertNotes } from './AlertNotes';
import { VerdictStrip, type HealthFilter } from './VerdictStrip';
import { PerformancePulse } from './PerformancePulse';
import { BookTable } from './BookTable';
import { WinsWorries } from './WinsWorries';
import { ChannelComparison } from './ChannelComparison';
import { CreativesCard } from './CreativesCard';
import { HomeFilterBar } from './HomeFilterBar';

const DEFAULT_PRESET: ReportingDatePreset = 'past_30';

export function AdvertisingHomePage(): React.JSX.Element {
  const user = useCurrentUser();
  const qc = useQueryClient();

  // Session state (like the reporting dashboards' filters): scope, range,
  // comparison mode, verdict filter.
  const [scope, setScope] = useState<string>('mine');
  const [preset, setPreset] = useState<ReportingDatePreset>(DEFAULT_PRESET);
  const [custom, setCustom] = useState<DateRange>(() => presetToRange(DEFAULT_PRESET, new Date()));
  const [comparisonMode, setComparisonMode] = useState<ComparisonMode>('previous');
  const [healthFilter, setHealthFilter] = useState<HealthFilter>(null);
  // The filter row (Brand · Country · Group · Account) — session state, like the
  // reporting dashboards' filters. Scope picks the book; these narrow within it.
  const [filterSel, setFilterSel] = useState<HomeFilterSelection>(EMPTY_HOME_FILTER);
  // The alerts panel's type-isolation chips — session state, default all on.
  // They filter the PANEL's list only; the table's status chips reflect ALL types.
  const [alertTypes, setAlertTypes] = useState<Set<AlertType>>(() => new Set(ALL_ALERT_TYPES));
  const [alertPlatform, setAlertPlatform] = useState<AlertPlatformFilter>('all');
  // ⚠️ The alert core reads NO clock, so the caller supplies today's UTC date
  // (see AlertInputs.utcToday). A LAZY INITIALISER, not a per-render
  // `new Date()`: the bound must not shift underneath a tab that happens to be
  // open as UTC midnight passes — the same posture as the portal's
  // `defaultPreset`. It must be the UTC date, never the browser's local one: a
  // UTC+10 browser's own date would put the bound on a day UTC has not
  // finished, which is exactly the partial day the bound exists to exclude.
  const [utcToday] = useState(() => new Date().toISOString().slice(0, 10));

  // Persisted view prefs (pacing toggle · display currency · sort) — the
  // gridPreferences envelope, seeded once (the save-on-change-only rule).
  const [view, setView] = useState<HomeViewState>(() =>
    decodeHomeViewState(user.gridPreferences?.[ADVERTISING_HOME_GRID_KEY]),
  );
  const saveView = useMutation({
    mutationFn: (next: HomeViewState) =>
      patchMyPreferences(ADVERTISING_HOME_GRID_KEY, encodeHomeViewState(next)),
    onSuccess: (u) => qc.setQueryData(['me'], u),
  });
  const commitView = (next: HomeViewState): void => {
    setView(next);
    saveView.mutate(next, {
      onError: (err) => console.error('Failed to save view preferences', err),
    });
  };

  // The morning Slack digest opt-in — DEFAULT OFF, and deliberately controlled
  // from this page rather than a settings screen: this is the page the digest
  // is about, so the toggle sits next to the thing it summarises.
  const slackDigest = useMutation({
    mutationFn: setSlackDigestEnabled,
    onSuccess: (u) => qc.setQueryData(['me'], u),
  });
  const digestEnabled = user.slackDigestEnabled ?? false;

  const range = useMemo(() => {
    try {
      return presetToRange(preset, new Date(), custom);
    } catch {
      return custom;
    }
  }, [preset, custom]);
  const compare = useMemo(() => comparisonRange(range, comparisonMode), [range, comparisonMode]);

  const overviewQuery = useQuery({
    queryKey: ['advertising-home-overview', scope],
    queryFn: () => getHomeOverview(scope),
    staleTime: 5 * 60 * 1000,
  });
  // The SAME query key both reporting dashboards use for the rate snapshot —
  // one cached read, one set of rates everywhere.
  const fxQuery = useQuery({
    queryKey: ['reporting-fx-rates'],
    queryFn: getFxRates,
    staleTime: 30 * 60 * 1000,
  });
  const fxSnapshot = fxQuery.data?.rates ?? null;
  const rangeValid = Boolean(range.from && range.to && range.from <= range.to);
  const bookQuery = useQuery({
    queryKey: ['advertising-home-book', scope, range.from, range.to, compare.from, compare.to],
    queryFn: () => getHomeBook(scope, range, compare),
    enabled: rangeValid,
    staleTime: 5 * 60 * 1000,
  });
  const creativesQuery = useQuery({
    queryKey: ['advertising-home-creatives', scope, range.from, range.to],
    // ⚠️ The AM gallery is WINNERS — cheapest CPL first, lead-less ads excluded.
    // The Meta specialist's waste panel asks the same endpoint for the inverse.
    queryFn: () => getHomeCreatives(scope, range, 'cheapest_cpl'),
    enabled: rangeValid,
    staleTime: 5 * 60 * 1000,
  });
  // Acknowledgements are TEAM-WIDE and scope-independent, so they get their own
  // (unscoped) query — an ack invalidates only this, never the BigQuery reads.
  const acksQuery = useQuery({
    queryKey: ['advertising-home-acks'],
    queryFn: listAcknowledgements,
    staleTime: 60 * 1000,
  });
  // ⚠⚠ THE HANDOFF NOTES, AND THIS IS THE HALF THAT WAS MISSING. Notes are
  // SURFACE-INDEPENDENT — keyed on the three-part alert identity with no board
  // in the key — so a note the Meta specialist writes on their own page is in
  // the collection and is returned here. It simply was never rendered, which
  // made the whole specialist→AM handoff a no-op that every test passed.
  //
  // ⚠️ THE SAME QUERY KEY the specialist boards use, so an AM opening this page
  // after a specialist has had theirs open pays for no second read.
  const notesQuery = useQuery({
    queryKey: ['advertising-home-alert-notes'],
    queryFn: listAlertNotes,
    staleTime: 60 * 1000,
  });
  const noteMutation = useMutation({
    mutationFn: createAlertNote,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['advertising-home-alert-notes'] });
    },
  });

  const invalidateAcks = (): void => {
    void qc.invalidateQueries({ queryKey: ['advertising-home-acks'] });
  };
  const ackMutation = useMutation({ mutationFn: acknowledgeAlert, onSuccess: invalidateAcks });
  const unackMutation = useMutation({ mutationFn: unacknowledgeAlert, onSuccess: invalidateAcks });
  const onAcknowledge = (a: HomeAlert): void => {
    // ⚠️ The severity is sent AS ACKNOWLEDGED — it is what a later escalation
    // is measured against, so it must be the severity actually on screen.
    ackMutation.mutate({
      accountId: a.accountId,
      channel: a.channel,
      alertType: a.type,
      surface: 'am',
      severity: a.severity,
    });
  };
  const onUnacknowledge = (entry: AcknowledgedAlert): void => {
    unackMutation.mutate(entry.acknowledgement.id);
  };

  const overview = overviewQuery.data;
  const book = bookQuery.data;
  const display = normalizeDisplayCurrency(view.displayCurrency, fxSnapshot);

  // ⚠️ ONE filtered account set + ONE row filter feed EVERY section below —
  // verdict, alerts, pulse, table, wins/worries, comparison, creatives — which
  // is what makes "the whole page reflects the filter" structural.
  const bookAccounts = useMemo(
    () => filterBookAccounts(overview?.accounts ?? [], filterSel),
    [overview, filterSel],
  );
  const overviewRows = useMemo(
    () => filterRowsToAccounts(overview?.rows ?? [], bookAccounts),
    [overview, bookAccounts],
  );
  const bookRangeRows = useMemo(
    () => filterRowsToAccounts(book?.rows ?? [], bookAccounts),
    [book, bookAccounts],
  );
  const bookCompareRows = useMemo(
    () => filterRowsToAccounts(book?.compareRows ?? [], bookAccounts),
    [book, bookAccounts],
  );

  const spine = useMemo(() => indexSpine(overviewRows), [overviewRows]);
  const evaluation = useMemo(
    () =>
      evaluateAlerts({
        accounts: bookAccounts,
        spine,
        asOf: overview?.asOf ?? { meta: null, google: null },
        utcToday,
        includePacing: view.pacing,
        brandAverages: new BrandAverages(overview?.brandWindows),
      }),
    [bookAccounts, spine, overview, utcToday, view.pacing],
  );

  const counts = useMemo(() => {
    const c = { total: 0, critical: 0, attention: 0, ontrack: 0 };
    for (const health of evaluation.healthByAccount.values()) {
      c.total += 1;
      c[health] += 1;
    }
    return c;
  }, [evaluation]);

  // ⚠️ The status popovers read from the FULL evaluation — every alert type,
  // acknowledged or not — because that is what each row's status was computed
  // from. The panel's filters narrow the QUEUE, never the table's ground truth.
  const notesByAlert = useMemo(
    () => groupNotesByAlert(notesQuery.data ?? []),
    [notesQuery.data],
  );

  const alertsByAccount = useMemo(() => {
    const map = new Map<string, HomeAlert[]>();
    for (const a of evaluation.alerts) {
      const list = map.get(a.accountId);
      if (list) list.push(a);
      else map.set(a.accountId, [a]);
    }
    return map;
  }, [evaluation]);

  // ⚠️ ACKNOWLEDGEMENT IS A FILTER OVER THE EVALUATION'S OUTPUT, never an input
  // to it: `evaluation` (and so the table's status chips + the verdict strip)
  // still reflects EVERY alert, acked or not. Only the panel's queue shrinks.
  const ackSplit = useMemo(
    // ⚠️ `'am'` — THIS BOARD. The specialist pages clear independently, so an
    // alert the Meta team has handled still shows here until an AM clears it
    // too. That is intended, and it means the counts on the two pages will
    // legitimately disagree — which is why each page says so on screen.
    () => applyAcknowledgements(evaluation.alerts, acksQuery.data ?? [], Date.now(), 'am'),
    [evaluation.alerts, acksQuery.data],
  );

  const byHealth = <T,>(items: T[], accountIdOf: (t: T) => string): T[] =>
    healthFilter === null
      ? items
      : items.filter((t) => evaluation.healthByAccount.get(accountIdOf(t)) === healthFilter);

  const visibleAlerts = useMemo(
    () => byHealth(ackSplit.visible, (a) => a.accountId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ackSplit.visible, evaluation, healthFilter],
  );
  const acknowledgedAlerts = useMemo(
    () => byHealth(ackSplit.acknowledged, (e) => e.alert.accountId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ackSplit.acknowledged, evaluation, healthFilter],
  );

  const pulse = useMemo(
    () => (overview ? buildPulse(overviewRows, overview.asOf) : []),
    [overview, overviewRows],
  );

  // The book's dominant native currency — what a money series is drawn in when
  // no display currency is active (a single series cannot draw two).
  const bookCurrency = useMemo(() => {
    const spendBy = new Map<string, number>();
    for (const r of overviewRows) spendBy.set(r.currency, (spendBy.get(r.currency) ?? 0) + r.spend);
    let best: string | null = null;
    let max = -Infinity;
    for (const [c, amount] of spendBy) {
      if (amount > max) {
        max = amount;
        best = c;
      }
    }
    return best;
  }, [overviewRows]);

  // Pacing for the table column (the alert core computes its own for alerts).
  const pacingByAccount = useMemo(() => {
    if (!view.pacing || !overview) return undefined;
    const map = new Map<string, { meta: ReturnType<typeof computeChannelPacing>; google: ReturnType<typeof computeChannelPacing> }>();
    for (const a of bookAccounts) {
      map.set(a.id, {
        meta: computeChannelPacing(
          channelDays(spine, a.id, 'meta'),
          a.metaMonthlyBudgetCents,
          overview.asOf.meta,
        ),
        google: computeChannelPacing(
          channelDays(spine, a.id, 'google'),
          a.googleMonthlyBudgetCents,
          overview.asOf.google,
        ),
      });
    }
    return map;
  }, [view.pacing, overview, bookAccounts, spine]);

  const bookRows = useMemo(() => {
    if (!overview || !book) return [];
    const rows = buildBookRows({
      accounts: bookAccounts,
      rangeRows: bookRangeRows,
      compareRows: bookCompareRows,
      healthByAccount: evaluation.healthByAccount,
      pacingByAccount: pacingByAccount ?? undefined,
      brandAverages: new BrandAverages(book.brandWindows),
    });
    const filtered =
      healthFilter === null ? rows : rows.filter((r) => r.health === healthFilter);
    return sortBookRows(filtered, view.sortKey, view.sortDir);
  }, [overview, book, bookAccounts, bookRangeRows, bookCompareRows, evaluation, pacingByAccount, healthFilter, view.sortKey, view.sortDir]);

  const winsWorries = useMemo(
    () => rankCplChanges(bookRangeRows, bookCompareRows),
    [bookRangeRows, bookCompareRows],
  );
  const creativeRows = useMemo(
    () => filterRowsToAccounts(creativesQuery.data?.rows ?? [], bookAccounts),
    [creativesQuery.data, bookAccounts],
  );
  const nameOf = useMemo(() => {
    const byId = new Map((overview?.accounts ?? []).map((a) => [a.id, a.name]));
    return (id: string) => byId.get(id) ?? id;
  }, [overview]);

  const onSort = (key: BookSortKey): void => {
    if (view.sortKey === key) {
      commitView({ ...view, sortDir: view.sortDir === 'asc' ? 'desc' : 'asc' });
    } else {
      // Numbers open descending (biggest first); name/status ascending.
      const dir = key === 'name' || key === 'attention' ? 'asc' : 'desc';
      commitView({ ...view, sortKey: key, sortDir: dir });
    }
  };

  const scopeOptions = useMemo(() => {
    const others = (overview?.amOptions ?? []).filter((o) => o.uid !== user.uid);
    return [
      { value: 'mine', label: 'My accounts' },
      { value: 'all', label: 'All accounts' },
      ...others.map((o) => ({ value: o.uid, label: o.name })),
    ];
  }, [overview, user.uid]);

  const unattributed = book?.unattributedGoogle;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Advertising Home"
        description="Your book's daily triage — alerts, trends, wins and worries across Meta + Google."
      />

      {/* Header controls: scope · range · comparison · currency · freshness. */}
      <div className="flex flex-wrap items-center gap-2">
        <SelectField
          size="sm"
          aria-label="Scope"
          value={scope}
          onValueChange={setScope}
          options={scopeOptions}
          className="min-w-[10rem]"
        />
        <DateRangePicker
          preset={preset}
          onPresetChange={setPreset}
          custom={custom}
          onCustomChange={setCustom}
        />
        <SelectField
          size="sm"
          aria-label="Comparison mode"
          value={comparisonMode}
          onValueChange={(v) => setComparisonMode(v as ComparisonMode)}
          options={COMPARISON_MODE_OPTIONS}
          className="min-w-[13rem]"
        />
        <DisplayCurrencySelect
          value={display}
          onChange={(next) => commitView({ ...view, displayCurrency: next })}
          snapshot={fxSnapshot}
          isAdmin={user.role === 'admin'}
        />
        <Button
          variant={view.pacing ? 'secondary' : 'outline'}
          size="sm"
          onClick={() => commitView({ ...view, pacing: !view.pacing })}
          aria-pressed={view.pacing}
        >
          Pacing {view.pacing ? 'on' : 'off'}
        </Button>
        <Button
          variant={digestEnabled ? 'secondary' : 'outline'}
          size="sm"
          disabled={slackDigest.isPending}
          onClick={() => slackDigest.mutate(!digestEnabled)}
          aria-pressed={digestEnabled}
          title={
            digestEnabled
              ? 'You get a Slack DM each weekday morning summarising this book. Alerts you acknowledge here are left out of it.'
              : 'Get a Slack DM each weekday morning summarising this book — the verdict, what needs action, and one win.'
          }
        >
          Slack digest {digestEnabled ? 'on' : 'off'}
        </Button>
        <span className="ml-auto text-xs text-muted-foreground">
          Meta data to {overview?.asOf.meta ?? '—'} · Google data to{' '}
          {overview?.asOf.google ?? '—'}
        </span>
      </div>
      {/* The filter row narrows WITHIN the scope's book — every section below
          derives from the one filtered set. Rendered whenever the book has
          accounts to filter (and stays visible over an empty filter result). */}
      {overview && overview.accounts.length > 0 && (
        <HomeFilterBar
          facets={accountFacetsFromBook(overview.accounts)}
          selection={filterSel}
          onChange={setFilterSel}
        />
      )}
      <ConversionNote display={display} snapshot={fxSnapshot} />

      {overviewQuery.isLoading && (
        <p className="text-sm text-muted-foreground">Loading your book…</p>
      )}
      {overviewQuery.isError && (
        <p className="text-sm text-destructive">
          Could not load the book — {(overviewQuery.error as Error).message}
        </p>
      )}

      {/* An EMPTY book is a normal state, never an error: say so, and point at
          the scope switcher (which stays live above) as the way out. */}
      {overview && overview.accounts.length === 0 && (
        <div className="rounded-lg border border-border bg-card p-6 text-sm">
          <p className="font-medium">
            {scope === 'mine'
              ? 'No accounts are assigned to you yet.'
              : 'No accounts in this scope.'}
          </p>
          <p className="mt-1 text-muted-foreground">
            Use the scope selector above to view another Account Manager's book, or pick
            {' '}
            <button
              type="button"
              onClick={() => setScope('all')}
              className="font-medium text-kyck-700 underline"
            >
              All accounts
            </button>
            .
          </p>
        </div>
      )}

      {overview && overview.accounts.length > 0 && (
        <>
          {/* 2 · the verdict strip */}
          <VerdictStrip counts={counts} filter={healthFilter} onFilter={setHealthFilter} />

          {/* 3 · alerts — collapsible (persisted) + type-isolation chips
              (session, panel-only). */}
          <AlertsPanel
            alerts={visibleAlerts}
            acknowledged={acknowledgedAlerts}
            /* Round 2 item 3 — external link icons beside each account name.
               Advertising Home only for now; the Meta/Google boards wait on a
               density look at a real screen. */
            showLinkIcons
            onAcknowledge={onAcknowledge}
            onUnacknowledge={onUnacknowledge}
            busy={ackMutation.isPending || unackMutation.isPending}
            collapsed={view.alertsCollapsed}
            onToggleCollapsed={() =>
              commitView({ ...view, alertsCollapsed: !view.alertsCollapsed })
            }
            typeFilter={alertTypes}
            onTypeFilterChange={setAlertTypes}
            platformFilter={alertPlatform}
            onPlatformFilterChange={setAlertPlatform}
            /*
             * ⚠⚠ ON THE OUTSTANDING ROWS, not only under the revealed
             * acknowledged group. An alert the Meta team has cleared is STILL
             * OUTSTANDING here — acknowledgements are per-surface — so a notes
             * block that only appeared beside an ack would be absent from
             * exactly the row the AM is about to act on, which is the handoff
             * this feature exists for.
             */
            renderAlertExtra={(a) => (
              <AlertNotes
                notes={notesByAlert.get(alertNoteKey(a)) ?? []}
                onAdd={(body) =>
                  noteMutation.mutate({
                    accountId: a.accountId,
                    channel: a.channel,
                    alertType: a.type,
                    body,
                  })
                }
                busy={noteMutation.isPending}
              />
            )}
          />

          {/* 4 · the performance pulse (fixed windows, range-independent).
              ONE metric × channel selection drives all three cards — they are
              the same question over three windows. */}
          <section className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <PanelTitle>Performance pulse</PanelTitle>
              <span className="ml-auto flex items-center gap-2">
                <SelectField
                  size="sm"
                  aria-label="Pulse metric"
                  value={view.pulseMetric}
                  onValueChange={(v) =>
                    isPulseMetric(v) && commitView({ ...view, pulseMetric: v })
                  }
                  options={PULSE_METRIC_OPTIONS}
                  className="min-w-[6.5rem]"
                />
                <SelectField
                  size="sm"
                  aria-label="Pulse channel"
                  value={view.pulseChannel}
                  onValueChange={(v) =>
                    isPulseChannel(v) && commitView({ ...view, pulseChannel: v })
                  }
                  options={PULSE_CHANNEL_OPTIONS}
                  className="min-w-[7rem]"
                />
              </span>
            </div>
            <PerformancePulse
              cards={pulse}
              display={display}
              snapshot={fxSnapshot}
              metric={view.pulseMetric}
              channel={view.pulseChannel}
              nativeCurrency={bookCurrency}
              channels={ALL_AD_CHANNELS}
            />
          </section>

          {/* Google attribution gap — the book must never silently under-report. */}
          {unattributed && unattributed.spend.length > 0 && (
            <p className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
              <span>
                {formatCurrencyAmounts(unattributed.spend)} of Google spend across{' '}
                {unattributed.campaigns} campaign{unattributed.campaigns === 1 ? '' : 's'} is
                unattributed this range —{' '}
                <Link to="/reporting/google-ads" className="font-medium underline">
                  fix match tokens
                </Link>
                .
              </span>
            </p>
          )}

          {/* 5 · the book */}
          <section className="space-y-2">
            <PanelTitle className="block">
              The book — {range.from} → {range.to} ({COMPARISON_MODE_OPTIONS.find((o) => o.value === comparisonMode)?.label})
            </PanelTitle>
            {bookQuery.isLoading ? (
              <p className="text-sm text-muted-foreground">Loading the range…</p>
            ) : (
              <BookTable
                rows={bookRows}
                alertsByAccount={alertsByAccount}
                sortKey={view.sortKey}
                sortDir={view.sortDir}
                onSort={onSort}
                showPacing={view.pacing}
                display={display}
                snapshot={fxSnapshot}
                channels={ALL_AD_CHANNELS}
                popoverLinkIcons
              />
            )}
          </section>

          {/* 6 · wins & worries */}
          {book && (
            <WinsWorries
              wins={winsWorries.wins}
              worries={winsWorries.worries}
              leadsBack={winsWorries.leadsBack}
              leadsGone={winsWorries.leadsGone}
              nameOf={nameOf}
            />
          )}

          {/* 7 · channel comparison */}
          {book && (
            <ChannelComparison rows={bookRangeRows} display={display} snapshot={fxSnapshot} />
          )}

          {/* 8 · best-performing Meta creatives */}
          {creativesQuery.data && (
            <CreativesCard
              rows={creativeRows}
              noLeadRows={filterRowsToAccounts(creativesQuery.data.noLeadRows ?? [], bookAccounts)}
              brandWindows={creativesQuery.data.brandWindows ?? []}
              brandOfAccount={(id) => bookAccounts.find((a) => a.id === id)?.brandAccountId ?? null}
              display={display}
              snapshot={fxSnapshot}
            />
          )}
        </>
      )}
    </div>
  );
}
